import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { useUnsavedGuard } from "../lib/unsavedGuard";
import Layout from "../components/Layout";
import { PlanBadge, PricingGrid, UpgradeButton, usePlans } from "../components/SubscriptionUI";
import { LocationsPanel } from "../components/LocationsPanel";
import { LogoUploader } from "../components/LogoUploader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Building2, Save, Globe, Upload, Download, FileSpreadsheet, Check, X, AlertTriangle, Clock, DollarSign, Trash2, ShieldCheck, Shield, Crown, ArrowLeftRight, Calendar, Copy, Mail, UsersRound, Eye, Sparkles, ChevronDown, MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import RoadmapTab from "../components/RoadmapTab";
import { SecurityPanel } from "../components/SecurityPanel";

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, refreshTenant, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [kioskDevices, setKioskDevices] = useState([]);
  const [ipInput, setIpInput] = useState("");
  const [paymentChecking, setPaymentChecking] = useState(false);
  // Compare Plans starts collapsed by default. Owners on paid plans rarely
  // need to see the pricing grid; collapsing keeps the Plan & Billing tab
  // short so the current-plan card + cancel section stay above the fold.
  // Auto-expanded on Free plan so new signups still see the upgrade path.
  const [plansExpanded, setPlansExpanded] = useState(false);
  const [dirty, setDirtyLocal] = useState(false);
  const [saved, setSaved] = useState(false);
  const [clockLinkCopied, setClockLinkCopied] = useState(false);
  const [regCode, setRegCode] = useState(null);  // { code, expires_at } when an active code exists
  const [regCodeLoading, setRegCodeLoading] = useState(false);
  const [regCodeRemaining, setRegCodeRemaining] = useState(0);
  const { setDirty: setGuardDirty, pending, proceed: proceedNav, cancel: cancelNav } = useUnsavedGuard();

  const setDirty = (v) => { setDirtyLocal(v); setGuardDirty(v); };

  // Current plan card pulls description + monthly price live from Super Admin CMS
  const { plans: cmsPlans } = usePlans();
  const currentPlanMeta = cmsPlans.find(p => p.id === (tenant?.plan || "free")) || null;

  // Block browser tab close / refresh / external nav
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Clear guard on unmount
  useEffect(() => () => setGuardDirty(false), [setGuardDirty]);

  const updateField = (patch) => { setFormData(prev => ({ ...prev, ...patch })); setDirty(true); setSaved(false); };

  const copyClockLink = () => {
    const url = `${window.location.origin}/clock/${tenant?.clock_code}`;
    navigator.clipboard.writeText(url);
    setClockLinkCopied(true);
    setTimeout(() => setClockLinkCopied(false), 2000);
  };

  // Check if returning from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setPaymentChecking(true);
      const poll = async (attempts = 0) => {
        try {
          const res = await apiClient.get(`/subscription/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            toast.success("Payment successful! Pro plan activated.");
            await refreshTenant();
            setPaymentChecking(false);
            navigate("/settings", { replace: true });
            return;
          }
          if (attempts < 5) setTimeout(() => poll(attempts + 1), 2000);
          else { setPaymentChecking(false); toast.error("Payment status unclear. Please check back."); }
        } catch { setPaymentChecking(false); }
      };
      poll();
    }
  }, [searchParams]);

  // Sync activeTab with ?tab=... query param (used by sidebar Coming Soon links).
  useEffect(() => {
    const requested = searchParams.get("tab");
    const allowed = ["company", "plan", "scheduling", "timeclock", "notifications", "import", "roadmap"];
    if (requested && allowed.includes(requested)) {
      setActiveTab(requested);
    }
  }, [searchParams]);

  const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const defaultStoreHours = () => DAYS_OF_WEEK.reduce((acc, d) => ({ ...acc, [d]: { open: "09:00", close: "22:00", closed: false } }), {});

  const [formData, setFormData] = useState({
    company_name: "",
    logo: "",
    timezone: "America/New_York",
    availability_deadline: "Sunday 7 PM",
    reminder_time: "Sunday 5 PM",
    allowed_ips: [],
    swap_policy: "schedule_group",
    schedule_visibility: "off",
    overtime_grace_minutes: 30,
    overtime_increment_minutes: 30,
    overtime_roundup_window_minutes: 5,
    swap_requires_approval: true,
    whatsapp_enabled: false,
    store_hours: defaultStoreHours(),
    auto_checkout_mode: "schedule",
    auto_checkout_fixed_hours: 12,
    auto_checkout_buffer_minutes: 30,
    auto_checkout_credit_policy: "zero",
    auto_checkout_edit_requires_approval: false,
  });

  useEffect(() => {
    if (tenant) {
      // Auto-expand Compare Plans for Free tenants — they're most likely to
      // want the upgrade path visible. Paid tenants get a collapsed grid.
      if (tenant.plan === "free" || !tenant.plan) setPlansExpanded(true);
      setFormData({
        company_name: tenant.company_name || "",
        logo: tenant.logo || "",
        timezone: tenant.timezone || "America/New_York",
        availability_deadline: tenant.availability_deadline || "Sunday 7 PM",
        reminder_time: tenant.reminder_time || "Sunday 5 PM",
        allowed_ips: tenant.allowed_ips || [],
        swap_policy: tenant.swap_policy || "schedule_group",
        schedule_visibility: tenant.schedule_visibility || "off",
        swap_requires_approval: tenant.swap_requires_approval !== false,
        whatsapp_enabled: !!tenant.whatsapp_enabled,
        store_hours: tenant.store_hours || defaultStoreHours(),
        auto_checkout_mode: tenant.auto_checkout_mode || "schedule",
        auto_checkout_fixed_hours: tenant.auto_checkout_fixed_hours ?? 12,
        auto_checkout_buffer_minutes: tenant.auto_checkout_buffer_minutes ?? 30,
        auto_checkout_credit_policy: tenant.auto_checkout_credit_policy || "zero",
        auto_checkout_edit_requires_approval: !!tenant.auto_checkout_edit_requires_approval,
        // Overtime grace policy — without these, the form resets to undefined
        // after saving and the inputs render blank (regression bug fix Feb 2026).
        overtime_grace_minutes: tenant.overtime_grace_minutes ?? 30,
        overtime_increment_minutes: tenant.overtime_increment_minutes ?? 30,
        overtime_roundup_window_minutes: tenant.overtime_roundup_window_minutes ?? 5,
        idle_timeout_minutes: tenant.idle_timeout_minutes ?? 5,
      });
      setDirty(false);
      setSaved(false);
      fetchKioskDevices();
    }
  }, [tenant]);

  const fetchKioskDevices = async () => {
    try {
      const res = await apiClient.get('/kiosk/devices');
      setKioskDevices(res.data.devices);
    } catch { /* ignore */ }
  };

  const handleRegisterDevice = async () => {
    try {
      const name = prompt("Device name (e.g., 'Front Counter iPad'):") || "Kiosk";
      const res = await apiClient.post('/kiosk/register', { device_name: name });
      // Store the token in localStorage for the clock page
      if (tenant?.clock_code) {
        localStorage.setItem(`kiosk_token_${tenant.clock_code}`, res.data.device_token);
      }
      toast.success(`Device "${res.data.device_name}" registered`);
      fetchKioskDevices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register device");
    }
  };

  const handleGenerateRegistrationCode = async () => {
    setRegCodeLoading(true);
    try {
      const res = await apiClient.post('/kiosk/registration-codes', {});
      setRegCode({ code: res.data.code, codeId: res.data.code_id, expiresAt: res.data.expires_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate code");
    } finally {
      setRegCodeLoading(false);
    }
  };

  // Live countdown — updates every second while a code is active.
  useEffect(() => {
    if (!regCode?.expiresAt) { setRegCodeRemaining(0); return; }
    const tick = () => {
      const ms = new Date(regCode.expiresAt).getTime() - Date.now();
      setRegCodeRemaining(Math.max(0, Math.floor(ms / 1000)));
      if (ms <= 0) setRegCode(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [regCode]);

  // While a code is outstanding, poll the device list so the UI flips
  // automatically the moment an onsite employee consumes the code on their
  // device — owner doesn't have to refresh.
  useEffect(() => {
    if (!regCode?.codeId) return;
    const marker = `registration_code:${regCode.codeId}`;
    const id = setInterval(async () => {
      try {
        const res = await apiClient.get('/kiosk/devices');
        const devices = res.data.devices || [];
        setKioskDevices(devices);
        const justRegistered = devices.find(d => d.registered_by === marker);
        if (justRegistered) {
          clearInterval(id);
          setRegCode(null);
          toast.success(`Device "${justRegistered.device_name}" registered`);
        }
      } catch { /* ignore transient errors */ }
    }, 3000);
    return () => clearInterval(id);
  }, [regCode?.codeId]);

  const handleRevokeDevice = async (deviceId) => {
    try {
      // Find the device token to clear from localStorage
      const device = kioskDevices.find(d => d.device_id === deviceId);
      await apiClient.delete(`/kiosk/devices/${deviceId}`);
      // Clear matching kiosk token from localStorage
      if (tenant?.clock_code && device) {
        const storedToken = localStorage.getItem(`kiosk_token_${tenant.clock_code}`);
        if (storedToken === device.device_token) {
          localStorage.removeItem(`kiosk_token_${tenant.clock_code}`);
        }
      }
      toast.success("Device revoked");
      fetchKioskDevices();
    } catch (e) {
      toast.error("Failed to revoke device");
    }
  };

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (ip && !(formData.allowed_ips || []).includes(ip)) {
      setFormData({ ...formData, allowed_ips: [...(formData.allowed_ips || []), ip] });
      setDirty(true);
      setSaved(false);
    }
    setIpInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/tenants/current', formData);
      await refreshTenant();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success("Settings saved");
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await apiClient.delete('/auth/account');
      toast.success("Account deleted");
      logout();
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  if (!tenant) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">No company setup. Please create a company first.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="settings-page" className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your company settings and import data</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="company" data-testid="settings-tab-company">
              <Building2 className="w-4 h-4 mr-1.5" /> Company
            </TabsTrigger>
            <TabsTrigger value="plan" data-testid="settings-tab-plan">
              <Crown className="w-4 h-4 mr-1.5" /> Plan & Billing
            </TabsTrigger>
            <TabsTrigger value="scheduling" data-testid="settings-tab-scheduling">
              <Calendar className="w-4 h-4 mr-1.5" /> Scheduling
            </TabsTrigger>
            <TabsTrigger value="timeclock" data-testid="settings-tab-timeclock">
              <Clock className="w-4 h-4 mr-1.5" /> Time Clock
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications">
              <Mail className="w-4 h-4 mr-1.5" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="settings-tab-security">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Security
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="settings-tab-import">
              <Upload className="w-4 h-4 mr-1.5" /> Import Data
            </TabsTrigger>
            <TabsTrigger value="roadmap" data-testid="settings-tab-roadmap">
              <Sparkles className="w-4 h-4 mr-1.5" /> Roadmap
            </TabsTrigger>
          </TabsList>

          {/* Company Settings Tab */}
          <TabsContent value="company" className="space-y-6 mt-4">
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#0D9488]" /> Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input value={formData.company_name} onChange={(e) => updateField({ company_name: e.target.value })} data-testid="settings-company-name" />
                </div>
                <LogoUploader
                  value={formData.logo || ""}
                  onChange={(dataUrl) => updateField({ logo: dataUrl })}
                  size={80}
                  label="Company Logo"
                />
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={formData.timezone} onValueChange={(val) => updateField({ timezone: val })}>
                    <SelectTrigger data-testid="settings-timezone"><Globe className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                      <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                      <SelectItem value="America/Phoenix">Arizona Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 bg-slate-50">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Tenant ID</p>
                    <p className="font-mono text-[#0A192F]">{tenant.tenant_id}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Currency</p>
                    <p className="text-[#0A192F]">{tenant.currency || "USD"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Locations Panel — Phase 1 multi-location rollout */}
            <LocationsPanel tenant={tenant} />

            {/* Danger Zone */}
            <Card className="border border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#E11D48]">
                  <AlertTriangle className="w-5 h-5" /> Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#0A192F]">Delete Account & Company</p>
                    <p className="text-sm text-slate-500">Permanently delete your account, company profile, and all associated data. This cannot be undone.</p>
                  </div>
                  <Button variant="outline" className="border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white shrink-0" onClick={() => setShowDeleteDialog(true)} data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Plan & Billing Tab */}
          <TabsContent value="plan" className="space-y-6 mt-4">
            <Card className={`border ${tenant?.plan === 'pro' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PlanBadge />
                    <div>
                      <p className="font-semibold text-[#0A192F]" data-testid="current-plan-name">
                        {currentPlanMeta?.name ? `${currentPlanMeta.name} Plan` : (tenant?.plan === 'pro' ? 'Pro Plan' : tenant?.plan === 'starter' ? 'Starter Plan' : 'Free Plan')}
                      </p>
                      <p className="text-xs text-slate-500" data-testid="current-plan-description">
                        {currentPlanMeta?.description || 'Loading plan details…'}
                      </p>
                    </div>
                  </div>
                  {currentPlanMeta && !currentPlanMeta.is_free && currentPlanMeta.price > 0 && (
                    <span
                      className={`text-sm font-medium ${tenant?.plan === 'pro' ? 'text-amber-600' : 'text-blue-600'}`}
                      data-testid="current-plan-price"
                    >
                      ${currentPlanMeta.price}/month
                    </span>
                  )}
                </div>
                {paymentChecking && (
                  <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-600 text-center animate-pulse">Verifying payment...</div>
                )}
                {/* Pending cancellation banner — appears once the owner has
                    scheduled a cancel-at-period-end. They can still resume
                    from here until the period actually ends. */}
                {tenant?.subscription_cancel_at_period_end && (
                  <CancelPendingBanner tenant={tenant} onResumed={refreshTenant} />
                )}
              </CardContent>
            </Card>

            {/* Cancel Subscription — only shown for paid tenants who aren't
                already scheduled to cancel. */}
            {tenant?.plan && tenant.plan !== "free" && !tenant.subscription_cancel_at_period_end && (
              <CancelSubscriptionCard tenant={tenant} onCancelled={refreshTenant} />
            )}

            <Card className="border border-slate-200">
              <CardHeader
                className="cursor-pointer select-none hover:bg-slate-50/50 transition-colors"
                onClick={() => setPlansExpanded((v) => !v)}
                data-testid="compare-plans-toggle"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" /> Compare Plans
                    </CardTitle>
                    <CardDescription>Pick the plan that fits your team — upgrade or downgrade any time.</CardDescription>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 transition-transform ${plansExpanded ? "rotate-180" : ""}`}
                    aria-hidden
                  />
                </div>
              </CardHeader>
              {plansExpanded && (
                <CardContent>
                  <PricingGrid currentPlan={tenant?.plan || "free"} />
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling" className="space-y-6 mt-4">

            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#0D9488]" /> Scheduling Defaults
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Availability Deadline</Label>
                  <Select value={formData.availability_deadline} onValueChange={(val) => updateField({ availability_deadline: val })}>
                    <SelectTrigger data-testid="settings-availability-deadline"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Saturday 7 PM">Saturday 7 PM</SelectItem>
                      <SelectItem value="Sunday 7 PM">Sunday 7 PM</SelectItem>
                      <SelectItem value="Sunday 9 PM">Sunday 9 PM</SelectItem>
                      <SelectItem value="Monday 12 PM">Monday 12 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reminder Time</Label>
                  <Select value={formData.reminder_time} onValueChange={(val) => updateField({ reminder_time: val })}>
                    <SelectTrigger data-testid="settings-reminder-time"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Saturday 5 PM">Saturday 5 PM</SelectItem>
                      <SelectItem value="Sunday 5 PM">Sunday 5 PM</SelectItem>
                      <SelectItem value="Sunday 7 PM">Sunday 7 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Team Schedule Visibility — lets employees view a read-only
                version of the team's weekly schedule for swap coordination
                and travel planning. Default off keeps existing behavior. */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#0D9488]" /> Team Schedule Visibility
                </CardTitle>
                <CardDescription>Let employees view a read-only weekly schedule of their teammates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">Enable Team Schedule view</p>
                    <p className="text-xs text-slate-500">When off, employees only see their own shifts (current behavior)</p>
                  </div>
                  <Switch
                    checked={formData.schedule_visibility !== "off"}
                    onCheckedChange={(on) => updateField({ schedule_visibility: on ? "same_group" : "off" })}
                    data-testid="schedule-visibility-toggle"
                  />
                </div>
                {formData.schedule_visibility !== "off" && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <Label>Who can each employee see?</Label>
                    <Select
                      value={formData.schedule_visibility}
                      onValueChange={(v) => updateField({ schedule_visibility: v })}
                    >
                      <SelectTrigger data-testid="schedule-visibility-scope-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="same_group">Same schedule group only (recommended)</SelectItem>
                        <SelectItem value="all">All employees</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Read-only. Shows name, shift name, time, and color — never pay info or notes.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Communication — WhatsApp deep-link (Alt D). Pro-only.
                Free tenants see the toggle disabled with an upgrade hint;
                Pro tenants can toggle freely. */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#0D9488]" /> Communication
                  {formData?.plan === "free" || tenant?.plan === "free" || !tenant?.plan ? (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                      PRO
                    </span>
                  ) : null}
                </CardTitle>
                <CardDescription>
                  Optional shortcut for owners who already use WhatsApp with their team.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">Show &quot;Message on WhatsApp&quot; buttons</p>
                    <p className="text-xs text-slate-500">
                      Adds a WhatsApp button on employee cards that opens a pre-composed message
                      to their phone. Employee must have a phone on file.
                    </p>
                  </div>
                  <Switch
                    checked={formData.whatsapp_enabled}
                    disabled={tenant?.plan === "free" || !tenant?.plan}
                    onCheckedChange={(on) => {
                      if (tenant?.plan === "free" || !tenant?.plan) {
                        toast.error("WhatsApp quick-message is a Pro feature. Upgrade to enable.");
                        return;
                      }
                      updateField({ whatsapp_enabled: on });
                    }}
                    data-testid="whatsapp-enabled-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Security — Auto-logout after inactivity. Configurable per
                tenant; enforced client-side by IdleTimeoutGuard.jsx with a
                60-second warning dialog before sign-out actually fires. */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#0D9488]" /> Security
                </CardTitle>
                <CardDescription>
                  Auto-logout protection for shared screens
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label>Auto-logout after inactivity</Label>
                  <Select
                    value={String(formData.idle_timeout_minutes)}
                    onValueChange={(v) => updateField({ idle_timeout_minutes: parseInt(v, 10) })}
                  >
                    <SelectTrigger
                      className="w-full sm:w-[260px]"
                      data-testid="idle-timeout-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 minutes (recommended)</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    A 60-second warning appears first — users click <span className="font-semibold">I&apos;m still here</span> to extend the session or get signed out automatically. Applies to all roles. Kiosk clock-in devices are unaffected.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Overtime Grace + Bucket Policy — controls how clock-out
                past a scheduled shift end translates to extra payable
                hours. Defaults: 30 min grace, 30 min increments, 5 min
                round-up window. */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0D9488]" /> Overtime Grace &amp; Rounding
                </CardTitle>
                <CardDescription>
                  How clock-outs past a scheduled shift end count toward extra pay (hourly employees only)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Grace period (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="240"
                      value={formData.overtime_grace_minutes}
                      onChange={(e) => updateField({ overtime_grace_minutes: parseInt(e.target.value || "0", 10) })}
                      data-testid="overtime-grace-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">No extra pay inside this window past shift end.</p>
                  </div>
                  <div>
                    <Label className="text-xs">Increment (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="240"
                      value={formData.overtime_increment_minutes}
                      onChange={(e) => updateField({ overtime_increment_minutes: parseInt(e.target.value || "0", 10) })}
                      data-testid="overtime-increment-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Bucket size after grace. Set to 0 to disable policy entirely.</p>
                  </div>
                  <div>
                    <Label className="text-xs">Round-up window (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max={formData.overtime_increment_minutes || 240}
                      value={formData.overtime_roundup_window_minutes}
                      onChange={(e) => updateField({ overtime_roundup_window_minutes: parseInt(e.target.value || "0", 10) })}
                      data-testid="overtime-roundup-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">If within this of the next bucket, round UP.</p>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="overtime-policy-preview">
                  <span className="font-semibold text-[#0A192F]">Example</span> · shift ends 10:00 PM · grace {formData.overtime_grace_minutes}m · increment {formData.overtime_increment_minutes}m · round-up {formData.overtime_roundup_window_minutes}m
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    <li>Clock out 10:25 → no extra (inside grace)</li>
                    <li>Clock out 10:45 → no extra (past grace but not within round-up of next bucket)</li>
                    <li>Clock out 10:55 → +30 min paid (within round-up of next bucket)</li>
                    <li>Clock out 11:29 → +60 min paid</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Shift Swap Config (Starter+) */}
            {(tenant?.plan === "starter" || tenant?.plan === "pro") ? (
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5 text-[#0D9488]" /> Shift Swap
                  </CardTitle>
                  <CardDescription>Control how employees can swap shifts with each other</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Swap Eligible With</Label>
                    <Select value={formData.swap_policy} onValueChange={(v) => updateField({ swap_policy: v })}>
                      <SelectTrigger data-testid="swap-policy-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Employee</SelectItem>
                        <SelectItem value="schedule_group">Same Schedule Group</SelectItem>
                        <SelectItem value="pay_group">Same Pay Group</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#0A192F]">Require Manager Approval</p>
                      <p className="text-xs text-slate-500">If off, swaps auto-execute when both employees agree</p>
                    </div>
                    <Switch checked={formData.swap_requires_approval} onCheckedChange={(v) => updateField({ swap_requires_approval: v })} data-testid="swap-approval-toggle" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-dashed border-slate-300 bg-slate-50/40">
                <CardContent className="p-5 flex items-start gap-3">
                  <Crown className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-[#0A192F]">Shift Swap is a Starter feature</p>
                    <p className="text-xs text-slate-500">Upgrade to Starter to let employees swap shifts with each other (with optional manager approval).</p>
                  </div>
                  <UpgradeButton size="sm" plan="starter" compact className="shrink-0" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Time Clock Tab */}
          <TabsContent value="timeclock" className="space-y-6 mt-4">

            {/* Store Hours & Auto-Checkout */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0D9488]" /> Store Hours & Auto Clock-Out
                </CardTitle>
                <CardDescription>Configure when forgotten clock-ins are auto-closed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Store Hours grid */}
                <div>
                  <Label className="mb-2 block">Store Hours</Label>
                  <div className="space-y-1.5">
                    {DAYS_OF_WEEK.map(day => {
                      const cfg = formData.store_hours?.[day] || { open: "09:00", close: "22:00", closed: false };
                      const updateDay = (patch) => updateField({ store_hours: { ...formData.store_hours, [day]: { ...cfg, ...patch } } });
                      const applyToAll = () => {
                        const next = {};
                        DAYS_OF_WEEK.forEach(d => { next[d] = { ...cfg }; });
                        updateField({ store_hours: next });
                        toast.success(`Applied ${day}'s hours to all days`);
                      };
                      return (
                        <div key={day} className="grid grid-cols-12 items-center gap-2 text-sm">
                          <div className="col-span-3 font-medium text-[#0A192F]">{day}</div>
                          <div className="col-span-2 flex items-center gap-1.5">
                            <Switch checked={!cfg.closed} onCheckedChange={(v) => updateDay({ closed: !v })} data-testid={`store-open-${day}`} />
                            <span className="text-xs text-slate-500">{cfg.closed ? "Closed" : "Open"}</span>
                          </div>
                          <Input type="time" value={cfg.open} onChange={(e) => updateDay({ open: e.target.value })} disabled={cfg.closed} className="col-span-2 text-sm h-9" data-testid={`store-open-time-${day}`} />
                          <span className="col-span-1 text-center text-slate-400">→</span>
                          <Input type="time" value={cfg.close} onChange={(e) => updateDay({ close: e.target.value })} disabled={cfg.closed} className="col-span-2 text-sm h-9" data-testid={`store-close-time-${day}`} />
                          <button
                            type="button"
                            onClick={applyToAll}
                            title={`Apply ${day}'s hours to every day of the week`}
                            className="col-span-2 inline-flex items-center justify-center gap-1 text-xs text-[#0D9488] hover:bg-teal-50 rounded px-2 py-1 transition-colors"
                            data-testid={`apply-all-${day}`}
                          >
                            <Copy className="w-3.5 h-3.5" /> Apply to all
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Auto-checkout policy (Starter+) */}
                {(tenant?.plan === "starter" || tenant?.plan === "pro") ? (
                <div className="border-t border-slate-100 pt-5 space-y-4">
                  <div className="space-y-2">
                    <Label>Auto-Close Trigger</Label>
                    <Select value={formData.auto_checkout_mode} onValueChange={(v) => updateField({ auto_checkout_mode: v })}>
                      <SelectTrigger data-testid="auto-checkout-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="schedule">Employee schedule end + buffer</SelectItem>
                        <SelectItem value="store_hours">Store close time + buffer</SelectItem>
                        <SelectItem value="fixed">Fixed hours after clock-in</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">When clock-in is forgotten, system auto-closes based on this rule.</p>
                  </div>

                  {formData.auto_checkout_mode === "fixed" ? (
                    <div className="space-y-2">
                      <Label>Auto-Close After (hours)</Label>
                      <Input type="number" min="1" max="24" value={formData.auto_checkout_fixed_hours} onChange={(e) => updateField({ auto_checkout_fixed_hours: parseInt(e.target.value) || 12 })} data-testid="auto-fixed-hours" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Buffer (minutes after {formData.auto_checkout_mode === "schedule" ? "shift end" : "store close"})</Label>
                      <Input type="number" min="0" max="180" value={formData.auto_checkout_buffer_minutes} onChange={(e) => updateField({ auto_checkout_buffer_minutes: parseInt(e.target.value) || 0 })} data-testid="auto-buffer-min" />
                      <p className="text-xs text-slate-500">Falls back to {formData.auto_checkout_fixed_hours}h fixed if data is missing.</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Hours Credited on Auto-Close</Label>
                    <Select value={formData.auto_checkout_credit_policy} onValueChange={(v) => updateField({ auto_checkout_credit_policy: v })}>
                      <SelectTrigger data-testid="auto-credit-policy"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zero">Zero hours (employee must confirm)</SelectItem>
                        <SelectItem value="scheduled">Scheduled shift hours (if scheduled)</SelectItem>
                        <SelectItem value="actual">Actual elapsed time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#0A192F]">Require Manager Approval to Edit Auto-Closed Entries</p>
                      <p className="text-xs text-slate-500">If on, employee edits go to a manager review queue.</p>
                    </div>
                    <Switch checked={formData.auto_checkout_edit_requires_approval} onCheckedChange={(v) => updateField({ auto_checkout_edit_requires_approval: v })} data-testid="auto-edit-approval-toggle" />
                  </div>
                </div>
                ) : (
                <div className="border-t border-slate-100 pt-5 flex items-start gap-3">
                  <Crown className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-[#0A192F]">Auto Clock-Out is a Starter feature</p>
                    <p className="text-xs text-slate-500">Upgrade to Starter or Pro to auto-close forgotten clock-ins based on schedule or store hours.</p>
                  </div>
                  <UpgradeButton size="sm" plan="starter" compact className="shrink-0" />
                </div>
                )}
              </CardContent>
            </Card>


            {/* Time Clock Link + Security */}
            {tenant?.clock_code && (
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#0D9488]" /> Employee Time Clock
                  </CardTitle>
                  <CardDescription>Share this link with employees. Only registered kiosk devices can clock in/out.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={`${window.location.origin}/clock/${tenant.clock_code}`}
                      className="font-mono text-sm bg-slate-50"
                      data-testid="time-clock-link"
                    />
                    <Button
                      variant={clockLinkCopied ? "default" : "outline"}
                      onClick={copyClockLink}
                      className={clockLinkCopied ? "bg-[#10B981] hover:bg-[#10B981] text-white" : ""}
                      data-testid="copy-clock-link-btn"
                    >
                      {clockLinkCopied ? <><Check className="w-4 h-4 mr-1.5" /> Copied!</> : "Copy"}
                    </Button>
                  </div>

                  {/* Register This Device */}
                  <div id="kiosk-devices" className="border-t border-slate-100 pt-4 scroll-mt-4">
                    <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                      <div>
                        <p className="font-medium text-sm text-[#0A192F]">Registered Kiosk Devices</p>
                        <p className="text-xs text-slate-500">Only these devices can use the time clock</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleGenerateRegistrationCode}
                          disabled={regCodeLoading}
                          data-testid="generate-reg-code-btn"
                          title="Generate a 6-digit code you can send to someone at another location so they can register that device without you being there."
                        >
                          {regCodeLoading ? "Generating…" : "Register Remote Device (Code)"}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#0D9488] hover:bg-[#14B8A6]"
                          onClick={handleRegisterDevice}
                          data-testid="register-device-btn"
                          title="Trust this browser right now — use this button on the very tablet or computer your employees will clock in from."
                        >
                          <ShieldCheck className="w-4 h-4 mr-1" /> Register This Device
                        </Button>
                      </div>
                    </div>

                    {/* Explainer — appears until at least one device is registered. */}
                    {kioskDevices.length === 0 && (
                      <div className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600 space-y-2" data-testid="kiosk-explainer">
                        <p className="font-semibold text-[#0A192F]">Two ways to trust a kiosk device:</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="p-2 bg-white rounded border border-slate-200">
                            <p className="font-semibold text-[#0D9488] mb-1">Register This Device</p>
                            <p>Use this when the tablet/computer employees will clock in on is <em>the one you&apos;re on right now</em>.</p>
                            <p className="text-slate-400 italic mt-1">e.g. Sitting at the counter iPad → click Register This Device. Done.</p>
                          </div>
                          <div className="p-2 bg-white rounded border border-slate-200">
                            <p className="font-semibold text-amber-700 mb-1">Register Remote Device</p>
                            <p>Generates a 6-digit code you can text/email to a manager at another store. They open your /clock link on their tablet, type the code, and it&apos;s trusted forever.</p>
                            <p className="text-slate-400 italic mt-1">e.g. Setting up a second location without traveling there.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {regCode && (
                      <div className="mb-3 p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="active-reg-code-card">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Registration Code</p>
                            <p className="text-3xl font-mono font-bold tracking-[0.3em] text-[#0A192F]" data-testid="reg-code-value">{regCode.code}</p>
                            <p className="text-xs text-amber-700 mt-2">
                              Share this code with the employee at your shop. They can enter it on the clock page to register their device.
                              {regCodeRemaining > 0 && (
                                <> Expires in <span className="font-semibold">{Math.floor(regCodeRemaining / 60)}:{String(regCodeRemaining % 60).padStart(2, "0")}</span>.</>
                              )}
                            </p>
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            className="shrink-0"
                            onClick={() => { navigator.clipboard.writeText(regCode.code); toast.success("Code copied"); }}
                            data-testid="copy-reg-code-btn"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    {kioskDevices.length > 0 ? (
                      <div className="space-y-2">
                        {kioskDevices.map(d => (
                          <div key={d.device_id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <ShieldCheck className="w-4 h-4 text-[#10B981]" />
                              <div>
                                <p className="text-sm font-medium text-[#0A192F]">{d.device_name}</p>
                                <p className="text-xs text-slate-400">
                                  {String(d.registered_by || "").startsWith("registration_code:")
                                    ? "Self-registered with code"
                                    : `By ${d.registered_by}`}
                                  {d.ip_at_registration ? ` · IP: ${d.ip_at_registration}` : ""}
                                  {d.last_used ? ` · Last used: ${new Date(d.last_used).toLocaleDateString()}` : ""}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="text-[#E11D48] hover:bg-red-50" onClick={() => handleRevokeDevice(d.device_id)} data-testid={`revoke-device-${d.device_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-3">No devices registered yet. Open the clock page on a kiosk and register it here.</p>
                    )}
                  </div>

                  {/* IP Whitelist */}
                  <div className="border-t border-slate-100 pt-4">
                    <div className="mb-2">
                      <span className="font-medium text-sm text-[#0A192F]">IP Whitelist <Badge variant="outline" className="ml-1 text-xs text-slate-400">Optional</Badge></span>
                      <p className="text-xs text-slate-500">If set, clock-in/out only works from these IPs (in addition to device check)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={ipInput}
                        onChange={(e) => setIpInput(e.target.value)}
                        placeholder="e.g., 203.0.113.50"
                        className="text-sm"
                        data-testid="ip-whitelist-input"
                      />
                      <Button variant="outline" size="sm" onClick={handleAddIp} disabled={!ipInput.trim()} data-testid="add-ip-btn">Add</Button>
                    </div>
                    {(formData.allowed_ips || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.allowed_ips.map(ip => (
                          <Badge key={ip} variant="outline" className="gap-1 text-slate-600">
                            {ip}
                            <button className="ml-1 text-slate-400 hover:text-[#E11D48]" onClick={() => updateField({ allowed_ips: formData.allowed_ips.filter(i => i !== ip) })}>
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 mt-4">
            <NotificationPreferences />
          </TabsContent>

          <TabsContent value="security" className="space-y-6 mt-4">
            <SecurityPanel />
          </TabsContent>

          <TabsContent value="import" className="space-y-6 mt-4">
            <ImportSection type="employees" />
            <ImportSection type="hours" />
            <ImportSection type="payments" />
          </TabsContent>

          <TabsContent value="roadmap" className="space-y-6 mt-4">
            <RoadmapTab />
          </TabsContent>
        </Tabs>

        {/* Persistent Save Bar (hidden on Import + Roadmap tabs) */}
        {activeTab !== "import" && activeTab !== "roadmap" && activeTab !== "security" && (
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || (!dirty && !saved)}
              className={saved ? "bg-[#10B981] hover:bg-[#10B981]" : "bg-[#0D9488] hover:bg-[#14B8A6] disabled:opacity-50"}
              data-testid="settings-save-btn"
            >
              {saving ? "Saving..." : saved ? <><Check className="w-4 h-4 mr-2" /> Saved!</> : <><Save className="w-4 h-4 mr-2" /> {dirty ? "Save Changes" : "Save Settings"}</>}
            </Button>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6 text-[#E11D48]" />
            </div>
            <DialogTitle className="text-center text-[#0A192F]">Delete Account?</DialogTitle>
            <DialogDescription className="text-center text-slate-500 pt-2">
              This will permanently delete your account, company (<span className="font-semibold">{tenant?.company_name}</span>), all employees, payments, schedules, and time entries. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm text-slate-600">Type <span className="font-mono font-bold text-[#E11D48]">DELETE</span> to confirm:</Label>
            <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteConfirm(""); }} data-testid="cancel-delete-account">Cancel</Button>
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white" disabled={deleteConfirm !== "DELETE" || deleting} onClick={handleDeleteAccount} data-testid="confirm-delete-account">
              {deleting ? "Deleting..." : "Delete Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Guard */}
      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) cancelNav(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="settings-unsaved-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Unsaved Changes
            </DialogTitle>
            <DialogDescription>You have unsaved settings. Save before leaving, or your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={cancelNav} data-testid="settings-unsaved-cancel">Stay on Page</Button>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => { setDirtyLocal(false); proceedNav(); }} data-testid="settings-unsaved-discard">
              Discard & Leave
            </Button>
            <Button
              className="bg-[#0D9488] hover:bg-[#14B8A6]"
              disabled={saving}
              onClick={async () => { await handleSave(); proceedNav(); }}
              data-testid="settings-unsaved-save"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save & Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ============ Notification Preferences Component ============
// Tenant-level keys are a subset of personal keys (no `marketing` — that's a
// per-user opt-in). Order shapes the UI groupings below.
const TENANT_NOTIFICATION_GROUPS = [
  {
    title: "Operations",
    description: "Day-to-day workflow alerts that fire across your company.",
    items: [
      { key: "auto_clockout_alerts", label: "Auto clock-out alerts", help: "Email managers when the system auto-closes a forgotten shift." },
      { key: "schedule_published",   label: "Schedule published",   help: "Notify employees when a new weekly schedule is posted." },
      { key: "availability_reminders", label: "Availability reminders", help: "Weekly nudge to employees who haven't submitted availability." },
      { key: "shift_swap",           label: "Shift swap notifications",  help: "Emails between employees + managers about swap requests." },
      { key: "payment_receipts",     label: "Payment receipts",     help: "Email employees a digital paystub when a payment is recorded." },
    ],
  },
  {
    title: "Account",
    description: "Team-management and billing communications.",
    items: [
      { key: "team_invites", label: "Team invite emails",       help: "Send invite links over email when adding teammates." },
      { key: "plan_updates", label: "Plan & billing updates",   help: "Subscription receipts, plan changes, expiry reminders." },
    ],
  },
];

const NOTIFICATION_GROUPS = [
  {
    title: "Operations",
    description: "Day-to-day workflow alerts. We recommend leaving these on.",
    items: [
      { key: "auto_clockout_alerts", label: "Auto clock-out alerts", help: "When the system auto-closes a forgotten shift." },
      { key: "schedule_published",   label: "Schedule published",   help: "When a new weekly schedule is posted." },
      { key: "availability_reminders", label: "Availability reminders", help: "Weekly nudge to submit your availability." },
      { key: "shift_swap",           label: "Shift swap requests",  help: "When someone wants to swap shifts with you, or your request is answered." },
      { key: "payment_receipts",     label: "Payment receipts",     help: "When a payment is recorded against your balance." },
    ],
  },
  {
    title: "Account",
    description: "Important account and team-management messages.",
    items: [
      { key: "team_invites", label: "Team invite confirmations", help: "When invites are accepted or declined." },
      { key: "plan_updates", label: "Plan & billing updates",     help: "Subscription changes, receipts, expiry reminders." },
    ],
  },
  {
    title: "Marketing",
    description: "Tips and product news. Off by default.",
    items: [
      { key: "marketing", label: "Product news & tips", help: "Occasional updates — never more than once a month." },
    ],
  },
];

const NotificationPreferences = () => {
  const { user } = useAuth();
  const canEditTenant = user?.role === "owner" || user?.role === "manager";
  const [prefs, setPrefs] = useState(null);
  const [tenantPrefs, setTenantPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiClient.get("/me/email-preferences").catch(() => null),
      apiClient.get("/tenants/notification-settings").catch(() => null),
    ]).then(([userRes, tenantRes]) => {
      if (cancelled) return;
      if (userRes) setPrefs(userRes.data.preferences);
      else toast.error("Could not load personal notification preferences");
      if (tenantRes) setTenantPrefs(tenantRes.data.notification_settings);
    });
    return () => { cancelled = true; };
  }, []);

  const saveUserPrefs = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      const res = await apiClient.put("/me/email-preferences", { preferences: next });
      setPrefs(res.data.preferences);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
      setPrefs(prefs); // revert
    } finally {
      setSaving(false);
    }
  };

  const saveTenantPrefs = async (next) => {
    setTenantPrefs(next);
    setSaving(true);
    try {
      const res = await apiClient.put("/tenants/notification-settings", { notification_settings: next });
      setTenantPrefs(res.data.notification_settings);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save company setting");
      setTenantPrefs(tenantPrefs); // revert
    } finally {
      setSaving(false);
    }
  };

  const togglePref = (key, val) => saveUserPrefs({ ...prefs, [key]: val });
  const toggleTenantPref = (key, val) => saveTenantPrefs({ ...tenantPrefs, [key]: val });

  if (!prefs) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="p-6 text-sm text-slate-400">Loading preferences…</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0D9488]" /> Email Notifications
          </CardTitle>
          <CardDescription>
            Two layers: <strong>company-wide</strong> toggles control which categories your team can ever receive; <strong>personal</strong> toggles control which of those <em>you</em> get. A category fires only when both layers are on.
            {saving && <span className="ml-2 text-xs text-slate-400">Saving…</span>}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Company-wide section — owner/manager only */}
      {canEditTenant && tenantPrefs && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <ShieldCheck className="w-4 h-4 text-[#0D9488]" />
            <h3 className="text-sm font-semibold text-[#0A192F] uppercase tracking-wider">Company-wide</h3>
            <span className="text-xs text-slate-400">— affects all team members</span>
          </div>
          {TENANT_NOTIFICATION_GROUPS.map(group => (
            <Card key={`tenant-${group.title}`} className="border border-amber-100 bg-amber-50/20" data-testid={`tenant-notif-card-${group.title}`}>
              <CardHeader>
                <CardTitle className="text-base">{group.title}</CardTitle>
                <CardDescription>{group.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {group.items.map(item => (
                  <div key={item.key} className="flex items-start justify-between gap-4 py-2 border-b border-amber-100/60 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A192F] text-sm">{item.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.help}</p>
                    </div>
                    <Switch
                      checked={!!tenantPrefs[item.key]}
                      onCheckedChange={(val) => toggleTenantPref(item.key, val)}
                      data-testid={`tenant-notif-toggle-${item.key}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Personal section — everyone */}
      <div className="space-y-4 pt-2">
        <div className="flex items-center gap-2 px-1">
          <UsersRound className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-[#0A192F] uppercase tracking-wider">Personal — only your inbox</h3>
        </div>
        {NOTIFICATION_GROUPS.map(group => (
          <Card key={`user-${group.title}`} className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">{group.title}</CardTitle>
              <CardDescription>{group.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {group.items.map(item => {
                const tenantOff = tenantPrefs && Object.prototype.hasOwnProperty.call(tenantPrefs, item.key) && !tenantPrefs[item.key];
                return (
                  <div key={item.key} className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A192F] text-sm">
                        {item.label}
                        {tenantOff && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-amber-700 border-amber-200 bg-amber-50">Company-wide off</Badge>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.help}</p>
                    </div>
                    <Switch
                      checked={!!prefs[item.key]}
                      onCheckedChange={(val) => togglePref(item.key, val)}
                      disabled={tenantOff}
                      data-testid={`notif-toggle-${item.key}`}
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Cancel Subscription — accessible to the tenant owner when they're on a paid
// plan. Collects a required cancellation reason + optional free-text feedback,
// hits POST /api/subscription/cancel (which flags Stripe to cancel_at_period_end
// so they keep paid features until the period ends), and refreshes the tenant.
// -----------------------------------------------------------------------------
const CANCEL_REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "missing_features", label: "Missing a feature I need" },
  { value: "switching_tools", label: "Switching to another tool" },
  { value: "not_using_enough", label: "Not using it enough" },
  { value: "temporary_pause", label: "Just need to pause for now" },
  { value: "closing_business", label: "Closing / pausing my business" },
  { value: "other", label: "Other (please tell us)" },
];

const CancelSubscriptionCard = ({ tenant, onCancelled }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error("Please pick a reason so we can improve.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/subscription/cancel", { reason, feedback });
      toast.success(res.data.message || "Subscription cancelled at end of period");
      setOpen(false);
      setReason("");
      setFeedback("");
      onCancelled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-red-200 bg-red-50/30" data-testid="cancel-subscription-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#E11D48] text-base">
          <AlertTriangle className="w-5 h-5" /> Cancel Subscription
        </CardTitle>
        <CardDescription>
          You&apos;ll keep full access to your <span className="font-medium capitalize">{tenant?.plan}</span> plan until the end of your current billing period. No refund for the current period, but nothing new will be charged.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setReason(""); setFeedback(""); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white" data-testid="open-cancel-dialog-btn">
              <AlertTriangle className="w-4 h-4 mr-2" /> Cancel Subscription
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel your {tenant?.plan} subscription?</DialogTitle>
              <DialogDescription>
                A quick note helps us improve Nirvanta for other business owners. You won&apos;t be charged again — your plan stays active until the end of this period.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Why are you cancelling? *</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="cancel-reason-select">
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CANCEL_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`cancel-reason-${r.value}`}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Anything else you&apos;d like to share? (optional)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g. Which feature would keep you on Nirvanta?"
                  data-testid="cancel-feedback-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting} data-testid="cancel-dialog-close-btn">
                Never mind
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !reason}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel-btn"
              >
                {submitting ? "Cancelling…" : "Confirm cancellation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const CancelPendingBanner = ({ tenant, onResumed }) => {
  const [resuming, setResuming] = useState(false);
  const cancelDate = tenant.subscription_cancel_at ? new Date(tenant.subscription_cancel_at) : null;
  const resume = async () => {
    setResuming(true);
    try {
      await apiClient.post("/subscription/resume");
      toast.success("Subscription resumed — thank you for staying!");
      onResumed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resume");
    } finally {
      setResuming(false);
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm" data-testid="cancel-pending-banner">
      <span className="text-amber-800">
        Cancellation scheduled. Your plan will end on{" "}
        <span className="font-semibold">
          {cancelDate ? cancelDate.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "the end of this period"}
        </span>
        . You still have full access until then.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-300 text-amber-800 hover:bg-amber-100"
        onClick={resume}
        disabled={resuming}
        data-testid="resume-subscription-btn"
      >
        {resuming ? "Resuming…" : "Undo cancellation"}
      </Button>
    </div>
  );
};


// ============ Import Section Component ============
const ImportSection = ({ type }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const meta = {
    employees: {
      title: "Import Employees",
      icon: <UsersRound className="w-5 h-5 text-[#0D9488]" />,
      description: "Bulk-add your team. The template auto-fills your Pay Groups and Schedule Groups on reference tabs so you can copy the exact group names. Required: First Name, Last Name, Hire Date (MM/DD/YYYY), Pay Group Name, Schedule Group Name, Pay Type, Pay Rate. Provide EITHER Email OR Phone — for phone-only staff, set SMS Opt-in to \"yes\".",
      fileLabel: "Employees",
    },
    hours: {
      title: "Import Hours",
      icon: <Clock className="w-5 h-5 text-[#0D9488]" />,
      description: "Upload historical net hours for employees. Template columns: Employee Name, Email, Worked Date (MM/DD/YYYY), Net Hours, Notes.",
      fileLabel: "Hours",
    },
    payments: {
      title: "Import Payments",
      icon: <DollarSign className="w-5 h-5 text-[#0D9488]" />,
      description: "Upload historical payment records. Template columns: Employee Name, Email, Payment Date (MM/DD/YYYY), Amount, Method, Notes.",
      fileLabel: "Payments",
    },
  }[type];

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get(`/import/template/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Nirvanta_${meta.fileLabel}_Import_Template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download template:", err);
      toast.error("Failed to download template");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post(`/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
    } catch (err) {
      console.error("Import failed:", err);
      setResult({ imported: 0, skipped: 0, errors: [{ row: "-", reason: err.response?.data?.detail || "Upload failed" }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Card className="border border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">{meta.icon} {meta.title}</CardTitle>
        <CardDescription>{meta.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid={`download-template-${type}`}>
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
          <div className="relative">
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleUpload}
              className="hidden"
              data-testid={`file-input-${type}`}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#0D9488] hover:bg-[#14B8A6]"
              data-testid={`upload-btn-${type}`}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Importing..." : "Upload & Import"}
            </Button>
          </div>
        </div>

        {/* Import Results */}
        {result && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-4">
              <Badge variant="default" className="bg-[#10B981] hover:bg-[#10B981]" data-testid={`import-success-${type}`}>
                <Check className="w-3 h-3 mr-1" /> {result.imported} imported
              </Badge>
              {result.skipped > 0 && (
                <Badge variant="destructive" data-testid={`import-skipped-${type}`}>
                  <AlertTriangle className="w-3 h-3 mr-1" /> {result.skipped} skipped
                </Badge>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-[#E11D48] mb-2">Skipped Rows:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-red-400 font-mono text-xs shrink-0">Row {err.row}:</span>
                      <span className="text-slate-700">{err.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Settings;
